import { connectorsForWallets, type Wallet } from '@rainbow-me/rainbowkit'
import { metaMaskWallet } from '@rainbow-me/rainbowkit/wallets'
import { createConfig, http, type CreateConnectorFn } from 'wagmi'
import { injected, walletConnect } from 'wagmi/connectors'
import {
  getEip6963MetaMaskProvider as getPluginEip6963MetaMaskProvider,
  resolveMetaMaskInjectedTarget,
  resolveRabbyInjectedTarget,
} from '@coti-io/coti-wallet-plugin'
import { mobileZerionWallet } from '@coti-io/coti-wallet-plugin/rainbowkit'
import { getRpcUrlForChain } from '@/chains'
import { resolveBraveWalletProvider, toSafeEip1193Provider } from '@/polyfills'
import {
  cotiTestnet,
  sepolia,
  cotiMainnet,
  avalancheFuji,
  ethereumMainnet,
  avalanche,
} from '@/config/chains'

// ─── WalletConnect project ID ─────────────────────────────────────────────
// Env var is the primary source; the hardcoded value is the fallback so the
// app works even when VITE_WALLETCONNECT_PROJECT_ID is not set in .env.
const WALLETCONNECT_PROJECT_ID: string =
  import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '14c0e808550e5a68bdb6579f3a406e0b'

// ─── Mobile UA helper ─────────────────────────────────────────────────────
const isMobileUA = (): boolean =>
  typeof navigator !== 'undefined' && (
    /android|iphone|ipod/i.test(navigator.userAgent) ||
    /ipad/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )

export const getEip6963MetaMaskProvider = () => {
  const p = getPluginEip6963MetaMaskProvider()
  return p ? toSafeEip1193Provider(p) : null
}

// ─── Shared WalletConnect connector factory ──────────────────────────────
let sharedWcConnectorInstance: ReturnType<CreateConnectorFn> | null = null
let sharedWcSetupPromise: Promise<void> | null = null
let sharedWcProviderPromise: Promise<unknown> | null = null
let cachedPortalWagmiConfig: ReturnType<typeof createConfig> | null = null

export const resetPortalWagmiConfigForTests = () => {
  sharedWcConnectorInstance = null
  sharedWcSetupPromise = null
  sharedWcProviderPromise = null
  cachedPortalWagmiConfig = null
}

const getSharedWcBase = (config: Parameters<CreateConnectorFn>[0]) => {
  if (!sharedWcConnectorInstance) {
    const raw = walletConnect({
      projectId: WALLETCONNECT_PROJECT_ID,
      showQrModal: false,
    })(config)
    const rawGetProvider = raw.getProvider.bind(raw)
    const rawSetup = raw.setup ? raw.setup.bind(raw) : undefined

    const dedupedGetProvider = ((...args: Parameters<typeof raw.getProvider>) => {
      if (!sharedWcProviderPromise) {
        sharedWcProviderPromise = Promise.resolve(rawGetProvider(...args)).catch((err) => {
          sharedWcProviderPromise = null
          throw err
        })
      }
      return sharedWcProviderPromise
    }) as typeof raw.getProvider

    const dedupedSetup = rawSetup
      ? () => {
          if (!sharedWcSetupPromise) {
            sharedWcSetupPromise = Promise.resolve(rawSetup()).catch((err) => {
              sharedWcSetupPromise = null
              throw err
            })
          }
          return sharedWcSetupPromise
        }
      : undefined

    sharedWcConnectorInstance = {
      ...raw,
      ...(dedupedSetup ? { setup: dedupedSetup } : {}),
      getProvider: dedupedGetProvider,
      connect: raw.connect.bind(raw),
      disconnect: raw.disconnect.bind(raw),
      getAccounts: raw.getAccounts.bind(raw),
      getChainId: raw.getChainId.bind(raw),
      isAuthorized: raw.isAuthorized.bind(raw),
      ...(raw.switchChain ? { switchChain: raw.switchChain.bind(raw) } : {}),
      onAccountsChanged: raw.onAccountsChanged.bind(raw),
      onChainChanged: raw.onChainChanged.bind(raw),
      ...(raw.onConnect ? { onConnect: raw.onConnect.bind(raw) } : {}),
      onDisconnect: raw.onDisconnect.bind(raw),
    }
  }
  return sharedWcConnectorInstance
}

const wcConnector = (walletDetails: any): CreateConnectorFn =>
  (config) => ({
    ...getSharedWcBase(config),
    ...walletDetails,
  })

const BRAVE_MISSING_PROVIDER = {
  request() {
    return Promise.reject(
      Object.assign(new Error('Brave Wallet not found — please open in Brave or enable Brave Wallet in brave://settings/web3'), { code: 4900 }),
    )
  },
}

const BRAVE_ICON_URL =
  'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2032%2032%22%3E%3Crect%20width%3D%2232%22%20height%3D%2232%22%20rx%3D%228%22%20fill%3D%22%23FB542B%22%2F%3E%3Cpath%20fill%3D%22%23fff%22%20d%3D%22M23.5%208.2l-1.6-2.2H10.1L8.5%208.2%206.8%2011l1.1%207.7%202.9%204.7L16%2026l5.2-2.6%202.9-4.7%201.1-7.7-1.7-2.8zM16%2023.4l-3.6-2.1-1.9-3.2-.6-4.8%202.4-1.4L16%2014l3.7-2.1%202.4%201.4-.6%204.8-1.9%203.2L16%2023.4z%22%2F%3E%3C%2Fsvg%3E'

// ─── Custom wallet definitions ────────────────────────────────────────────

/**
 * EIP-6963 MetaMask — desktop only (extension-injected).
 * On mobile the built-in metaMaskWallet handles deep linking via WC.
 */
const eip6963MetaMaskWallet = (): Wallet => ({
  id: 'io.metamask',
  name: 'MetaMask',
  rdns: 'io.metamask',
  iconUrl: '/metamask-logo.svg',
  iconBackground: '#fff',
  hidden: () => isMobileUA(),
  downloadUrls: {
    chrome: 'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn',
    browserExtension: 'https://metamask.io/download/',
  },
  createConnector: (walletDetails) =>
    (config) => {
      const base = injected({
        target() {
          const resolved = resolveMetaMaskInjectedTarget()
          return {
            ...resolved,
            provider: toSafeEip1193Provider(resolved.provider),
          } as any
        },
      })(config)
      return {
        ...base,
        ...walletDetails,
        connect: base.connect.bind(base),
        disconnect: base.disconnect.bind(base),
        getProvider: base.getProvider.bind(base),
        getAccounts: base.getAccounts.bind(base),
        getChainId: base.getChainId.bind(base),
      }
    },
})

/**
 * Brave Wallet — EIP-6963 (`com.brave.wallet`) + `window.braveEthereum` / `window.ethereum.isBraveWallet`.
 */
const braveWalletCustom = (): Wallet => {
  const detected = resolveBraveWalletProvider()
  const isBraveInstalled = Boolean(
    detected ||
    (typeof navigator !== 'undefined' && (navigator as any).brave),
  )

  return {
    id: 'com.brave.wallet',
    name: 'Brave Wallet',
    rdns: 'com.brave.wallet',
    iconUrl: BRAVE_ICON_URL,
    iconBackground: '#FB542B',
    installed: isBraveInstalled || undefined,
    downloadUrls: {
      chrome: 'https://brave.com/wallet/',
      browserExtension: 'https://brave.com/wallet/',
      mobile: 'https://brave.com/wallet/',
      qrCode: 'https://brave.com/wallet/',
    },
    extension: {
      instructions: {
        learnMoreUrl: 'https://brave.com/wallet/',
        steps: [
          { step: 'install' as const, title: 'Open Brave Browser', description: 'Open this page in Brave Browser and enable Brave Wallet in Settings -> Web3.' },
          { step: 'create' as const, title: 'Create or unlock wallet', description: 'Unlock your Brave Wallet or import your account.' },
          { step: 'refresh' as const, title: 'Refresh this page', description: 'Refresh the page and click Brave Wallet to connect.' },
        ],
      },
    },
    createConnector: (walletDetails) =>
      (config) => {
        const base = injected({
          target() {
            const p = resolveBraveWalletProvider()
            return {
              id: 'com.brave.wallet',
              name: 'Brave Wallet',
              provider: (p ?? BRAVE_MISSING_PROVIDER) as any,
            }
          },
        })(config)
        return {
          ...base,
          ...walletDetails,
          connect: base.connect.bind(base),
          disconnect: base.disconnect.bind(base),
          getProvider: base.getProvider.bind(base),
          getAccounts: base.getAccounts.bind(base),
          getChainId: base.getChainId.bind(base),
        }
      },
  }
}

/**
 * Rabby wallet — injected when Rabby provides the provider, WalletConnect + QR otherwise.
 *
 * Built-in rabbyWallet sets installed:false outside Rabby's in-app browser, which hides
 * Rabby from Safari/Chrome mobile lists. This factory keeps Rabby visible everywhere:
 *   - Rabby in-app browser / desktop extension → injected connector
 *   - Safari / Chrome mobile → WalletConnect QR (scan from Rabby's WC scanner)
 *
 * No mobile.getUri — custom schemes and universal links are blocked or unreliable from
 * external mobile browsers; QR is the stable path (same as picking WalletConnect on testnet).
 */
const rabbyWalletWithMobile = (): Wallet => {
  const isRabbyInjected = typeof window !== 'undefined' &&
    !!(window as any)?.ethereum?.isRabby
  const shouldUseWalletConnect = !isRabbyInjected
  return {
    id: 'rabby',
    name: 'Rabby Wallet',
    rdns: 'io.rabby',
    iconUrl: `https://explorer-api.walletconnect.com/v3/logo/md/255e6ba2-8dfd-43ad-e88e-57cbb98f6800?projectId=${WALLETCONNECT_PROJECT_ID}`,
    iconBackground: '#8697FF',
    // Never installed:false — RainbowKit hides the wallet on mobile Safari/Chrome.
    installed: isRabbyInjected || undefined,
    downloadUrls: {
      android: 'https://play.google.com/store/apps/details?id=com.debank.rabbymobile',
      ios: 'https://apps.apple.com/us/app/rabby-wallet-crypto-evm/id6474381673',
      mobile: 'https://rabby.io/download',
      qrCode: 'https://rabby.io/download',
      chrome: 'https://chrome.google.com/webstore/detail/rabby-wallet/acmacodkjbdgmoleebolmdjonilkdbch',
      browserExtension: 'https://rabby.io',
    },
    qrCode: shouldUseWalletConnect
      ? {
          getUri: (uri: string) => uri,
          instructions: {
            learnMoreUrl: 'https://rabby.io/',
            steps: [
              { step: 'install' as const, title: 'Open the Rabby app', description: 'Download Rabby on your mobile device from the App Store or Google Play.' },
              { step: 'create' as const, title: 'Open WalletConnect scanner', description: 'Inside Rabby, tap the scan icon to open the WalletConnect scanner.' },
              { step: 'scan' as const, title: 'Scan the QR code', description: 'Scan the QR code shown on this screen to connect your wallet.' },
            ],
          },
        }
      : undefined,
    extension: {
      instructions: {
        learnMoreUrl: 'https://rabby.io/',
        steps: [
          { step: 'install' as const, title: 'Install the Rabby extension', description: 'Install Rabby Wallet from the Chrome Web Store.' },
          { step: 'create' as const, title: 'Create or import a wallet', description: 'Set up your wallet in the Rabby browser extension.' },
          { step: 'refresh' as const, title: 'Refresh this page', description: 'Refresh the page to connect with Rabby Wallet.' },
        ],
      },
    },
    createConnector: (walletDetails) =>
      shouldUseWalletConnect
        ? wcConnector(walletDetails)
        : (config) => {
            const base = injected({
              target() {
                const resolved = resolveRabbyInjectedTarget()
                return {
                  ...resolved,
                  provider: toSafeEip1193Provider(resolved.provider),
                } as any
              },
            })(config)
            return {
              ...base,
              ...walletDetails,
              connect: base.connect.bind(base),
              disconnect: base.disconnect.bind(base),
              getProvider: base.getProvider.bind(base),
              getAccounts: base.getAccounts.bind(base),
              getChainId: base.getChainId.bind(base),
            }
          },
  }
}

/**
 *
 * RainbowKit's built-in oneKeyWallet is injected-only (no mobile.getUri).
 * This definition adds mobile support using OneKey's registered WC links.
 *
 * Source: WalletConnect explorer (id: 1aedbcfc1f31...)
 *   native:    "onekey-wallet://"  →  onekey-wallet://wc?uri=<encoded>  ← confirmed working (Zama)
 *   universal: "https://app.onekey.so/wc/connect/wc"  →  did not work in practice
 *   docs:      https://developer.onekey.so/en/connect-to-software/mobile-deeplinks/
 *   icon:      WalletConnect explorer CDN (image_id: 2067c771-93e8-4b32-b388-b2a0e1d4dc00)
 */
const oneKeyWalletWithMobile = (): Wallet => {
  const isOneKeyInjected = typeof window !== 'undefined' &&
    !!(window as any)?.['$onekey']
  const shouldUseWalletConnect = !isOneKeyInjected

  return {
    id: 'onekey',
    name: 'OneKey',
    rdns: 'so.onekey.app.wallet',
    // Icon from the WalletConnect explorer CDN — the authoritative source
    iconUrl: `https://explorer-api.walletconnect.com/v3/logo/md/2067c771-93e8-4b32-b388-b2a0e1d4dc00?projectId=${WALLETCONNECT_PROJECT_ID}`,
    iconBackground: '#fff',
    iconAccent: '#00B812',
    installed: isOneKeyInjected || undefined,
    downloadUrls: {
      android: 'https://play.google.com/store/apps/details?id=so.onekey.app.wallet',
      ios: 'https://apps.apple.com/us/app/onekey-open-source-wallet/id1609559473',
      mobile: 'https://www.onekey.so/download/',
      qrCode: 'https://www.onekey.so/download/',
      chrome: 'https://chrome.google.com/webstore/detail/onekey/jnmbobjmhlngoefaiojfljckilhhlhcj',
      browserExtension: 'https://www.onekey.so/download/',
    },
    mobile: {
      // Confirmed working by Zama (app.zama.org): onekey-wallet://wc?uri=<encoded>
      // Universal link https://app.onekey.so/wc/connect/wc?uri=... did not work in practice.
      // Native custom scheme is the reliable path on both iOS and Android.
      getUri: shouldUseWalletConnect
        ? (uri: string) => `onekey-wallet://wc?uri=${encodeURIComponent(uri)}`
        : undefined,
    },
    qrCode: shouldUseWalletConnect
      ? {
          getUri: (uri: string) => uri,
          instructions: {
            learnMoreUrl: 'https://help.onekey.so/hc/en-us/categories/360000170236',
            steps: [
              { step: 'install' as const, title: 'Open the OneKey app', description: 'Download OneKey on your mobile device from the App Store or Google Play.' },
              { step: 'create' as const,  title: 'Create or import a wallet', description: 'Set up your wallet inside the OneKey mobile app.' },
              { step: 'scan' as const,    title: 'Scan the QR code', description: 'Tap the scan icon in OneKey and scan the QR code to connect.' },
            ],
          },
        }
      : undefined,
    createConnector: (walletDetails) =>
      shouldUseWalletConnect
        ? wcConnector(walletDetails)
        : (config) => ({
            ...injected({
              target: {
                id: 'onekey',
                name: 'OneKey',
                provider: toSafeEip1193Provider((window as any)?.['$onekey']?.ethereum),
              },
            })(config),
            ...walletDetails,
          }),
  }
}

/**
 * Zerion — injected on desktop (window.zerionWallet), WalletConnect deep link on
 * mobile. Implementation lives in the wallet plugin; wrapped here because it takes
 * a projectId argument unlike the zero-arg factories above. When Zerion falls back
 * to WalletConnect, route through getSharedWcBase(config) so WalletConnect Core is
 * only initialized once across Rabby, OneKey, and Zerion.
 */
const WALLET_CONNECT_FAILURE_EVENT = 'coti-wallet-plugin:wallet-connect-failure'

const isUserCancellationError = (message: string): boolean =>
  /user rejected|user disconnected|connection request reset/i.test(message)

const dispatchZerionConnectFailure = (error: unknown): void => {
  if (typeof window === 'undefined') return
  const message = error instanceof Error ? error.message : String(error)
  if (isUserCancellationError(message)) return
  window.dispatchEvent(
    new CustomEvent(WALLET_CONNECT_FAILURE_EVENT, {
      detail: { walletId: 'zerion', message },
    }),
  )
}

const zerionWallet = (): Wallet => {
  const baseWallet = mobileZerionWallet({
    projectId: WALLETCONNECT_PROJECT_ID,
  }) as unknown as Wallet
  const isZerionInjected =
    typeof window !== 'undefined' && !!(window as any)?.zerionWallet
  const shouldUseWalletConnect = isMobileUA() || !isZerionInjected

  if (!shouldUseWalletConnect) {
    return baseWallet
  }

  return {
    ...baseWallet,
    createConnector: (walletDetails) => (config) => {
      const sharedWc = getSharedWcBase(config)
      return {
        ...sharedWc,
        ...walletDetails,
        connect: (async (parameters: any = {}) => {
          try {
            return await sharedWc.connect({ ...parameters, chainId: undefined })
          } catch (error) {
            dispatchZerionConnectFailure(error)
            throw error
          }
        }) as typeof sharedWc.connect,
      }
    },
  }
}

// Desktop and mobile use disjoint wallet lists so the MetaMask SDK connector is not
// registered alongside EIP-6963 MetaMask on desktop.
function getPortalWalletGroups() {
  if (isMobileUA()) {
    return [
      {
        groupName: 'Recommended',
        wallets: [metaMaskWallet, braveWalletCustom],
      },
      {
        groupName: 'Other Wallets',
        wallets: [
          rabbyWalletWithMobile,
          zerionWallet,
          oneKeyWalletWithMobile,
        ],
      },
    ]
  }

  return [
    {
      groupName: 'Recommended',
      wallets: [eip6963MetaMaskWallet, braveWalletCustom],
    },
    {
      groupName: 'Other Wallets',
      wallets: [
        rabbyWalletWithMobile,
        oneKeyWalletWithMobile,
        zerionWallet,
      ],
    },
  ]
}

export function getPortalWagmiConfig() {
  if (cachedPortalWagmiConfig) {
    return cachedPortalWagmiConfig
  }
  const connectors = connectorsForWallets(getPortalWalletGroups(), {
    appName: 'COTI Privacy Bridge',
    projectId: WALLETCONNECT_PROJECT_ID,
  })
  cachedPortalWagmiConfig = createConfig({
    // Include testnet + mainnet L1s so a single build can reconnect to either
    // set; NetworkSelector chooses which ones users see via VITE_DEFAULT_NETWORK_ID.
    chains: [cotiTestnet, sepolia, cotiMainnet, avalancheFuji, ethereumMainnet, avalanche],
    connectors,
    multiInjectedProviderDiscovery: false,
    ssr: false,
    transports: {
      [sepolia.id]: http(
        import.meta.env.VITE_SEPOLIA_RPC_URL || 'https://ethereum-sepolia-rpc.publicnode.com'
      ),
      [cotiTestnet.id]: http(
        import.meta.env.VITE_COTI_TESTNET_RPC_URL || 'https://testnet.coti.io/rpc'
      ),
      [cotiMainnet.id]: http(
        import.meta.env.VITE_COTI_MAINNET_RPC_URL || 'https://mainnet.coti.io/rpc'
      ),
      [avalancheFuji.id]: http(getRpcUrlForChain(avalancheFuji.id)),
      [ethereumMainnet.id]: http(
        import.meta.env.VITE_ETHEREUM_MAINNET_RPC_URL || getRpcUrlForChain(ethereumMainnet.id)
      ),
      [avalanche.id]: http(
        import.meta.env.VITE_AVALANCHE_RPC_URL || getRpcUrlForChain(avalanche.id)
      ),
    },
  })
  return cachedPortalWagmiConfig
}
